/**
 * IgnoredRulesSection — bordered table of (rule_id, match_text) phrase
 * ignores. Read + remove only (adds happen from the spellcheck
 * flyout's "Ignore" button on grammar suggestions). Same Figma
 * (node 760:21069) table treatment as DictionarySection.
 */

import React, { useEffect, useState } from "react";
import Auth from "src/Auth";
import IconButton from "src/components/IconButton";
import { getCurrentEditor } from "src/lib/Editor";
import { cn } from "src/lib/cn";

interface IgnoredRule {
  id: string;
  rule_id: string;
  rule_description: string | null;
  action: string;
  match_text: string | null;
  document_id: string | null;
  created_at: string;
}

/**
 * Fallback when the row has no rule_description (older data written
 * before SW-9064). Turn "PERS_PRONOUN_AGREEMENT" → "Pers pronoun
 * agreement" — not great, but readable enough as a last resort.
 */
function humanizeRuleId(ruleId: string): string {
  const lower = ruleId.toLowerCase().replace(/_/g, " ").trim();
  if (!lower) return ruleId;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function displayLabel(rule: IgnoredRule): string {
  if (rule.rule_description && rule.rule_description.trim()) {
    return rule.rule_description.trim();
  }
  return humanizeRuleId(rule.rule_id);
}

export function IgnoredRulesSection() {
  const [rules, setRules] = useState<IgnoredRule[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Auth.fetchWithIdToken("/api/style-settings/ignored-rules/list", {})
      .then((data: any) => {
        if (!cancelled) {
          if (data?.rules) setRules(data.rules);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onDelete = async (id: string, ruleId: string, matchText: string | null) => {
    const prev = rules;
    setRules((rows) => rows.filter((r) => r.id !== id));
    try {
      await Auth.fetchWithIdToken("/api/style-settings/ignored-rules/delete", { id });
      const editor = getCurrentEditor?.();
      if (editor?.commands?.removeLocalStyleRule) {
        editor.commands.removeLocalStyleRule({ ruleId, matchText });
      }
      if (editor?.commands?.recheckGrammarFull) {
        editor.commands.recheckGrammarFull(true);
      }
    } catch {
      setRules(prev);
    }
  };

  if (!loaded) {
    return <p className="m-0 text-sm text-app-grey-1 font-inter">Loading…</p>;
  }

  if (rules.length === 0) {
    return (
      <p className="m-0 text-sm text-app-grey-1 font-inter [text-wrap:pretty]">
        Phrases you choose to ignore will appear here. You can remove them at
        any time to start seeing them flagged again.
      </p>
    );
  }

  return (
    <div
      className="rounded-lg border border-solid border-app-grey-3 overflow-hidden bg-app-menu-color"
      role="table"
      aria-label="Ignored rules"
    >
      {rules.map((row, index) => {
        const label = displayLabel(row);
        const accessibleName = row.match_text || label;
        return (
          <div
            key={row.id}
            role="row"
            className={cn(
              "flex items-center gap-3 h-14 px-4",
              index > 0 && "border-t border-solid border-app-grey-3"
            )}
          >
            <span className="flex-1 truncate text-sm text-app-grey-0 font-inter">
              {row.match_text ? `“${row.match_text}”` : label}
            </span>
            {row.match_text && (
              <span className="text-xs text-app-grey-2 font-inter truncate max-w-[55%]">
                {label}
              </span>
            )}
            <IconButton
              size={32}
              iconSize={16}
              src="/assets/close.svg"
              smartFill
              aria-label={`Remove ${accessibleName}`}
              onClick={() => onDelete(row.id, row.rule_id, row.match_text)}
            />
          </div>
        );
      })}
    </div>
  );
}
